/**
 * The Gear rail's shopping-page groupings — pure functions only, no DOM, no
 * catalogue-loading concerns (those stay in web/src/components/room/gear).
 * Implements docs/plans/2026-09-15-gear-v2-brief.md §3-5: one category per
 * display section, four picks spread across price for the home view, and
 * the price/type filters the category page and search results share.
 *
 * `tv` / `projector` / `projector_screen` are not on `ProductType` yet
 * (schema.ts — another agent is adding them), so every type string here is
 * plain `string` rather than `ProductType`, and category membership compares
 * `(p.type as string)`. Once those three land on `PRODUCT_TYPES`, tighten
 * `BrowseType.type` to `ProductType` and drop the casts.
 */
import { SPEAKER_TYPES, type Product } from './schema';

export type CategoryKey =
  | 'speakers'
  | 'subwoofers'
  | 'receivers'
  | 'amplifiers'
  | 'tvs'
  | 'projectors'
  | 'screens';

export interface BrowseType {
  /** A `ProductType` value today; will include `tv`/`projector`/
   *  `projector_screen` once schema.ts adds them — see file doc. */
  type: string;
  label: string;
}

export interface BrowseCategory {
  key: CategoryKey;
  label: string;
  /** This category's type chips, in display order. */
  types: readonly BrowseType[];
}

/**
 * Duplicated from `TYPE_LABELS` (web/src/components/room/gear/labels.ts) —
 * same reason search.ts's own private copy gives: av-catalog is a
 * dependency-free leaf package and cannot import from `web`. Must stay
 * byte-identical for the ten existing types; `tv`/`projector`/
 * `projector_screen` are new (brief §6) and have no `web` twin yet.
 */
const TYPE_LABEL: Record<string, string> = {
  speaker_bookshelf: 'Bookshelf',
  speaker_tower: 'Tower',
  speaker_center: 'Center',
  speaker_surround: 'Surround',
  speaker_height: 'Height',
  speaker_in_ceiling: 'In-ceiling',
  subwoofer: 'Subwoofer',
  av_receiver: 'Receiver',
  av_processor: 'Processor',
  power_amp: 'Power amp',
  tv: 'TV',
  projector: 'Projector',
  projector_screen: 'Screen',
};

function browseType(type: string): BrowseType {
  return { type, label: TYPE_LABEL[type] ?? type };
}

/** Home-view section order — brief §5's numbered list, exactly. */
export const BROWSE_CATEGORIES: readonly BrowseCategory[] = [
  { key: 'speakers', label: 'Speakers', types: SPEAKER_TYPES.map(browseType) },
  { key: 'subwoofers', label: 'Subwoofers', types: [browseType('subwoofer')] },
  {
    // Label per docs/plans/2026-09-16-gear-requirements-brief.md §1 "Where
    // these appear" — a receiver and a processor share this section, so the
    // header must name both rather than implying every unit here has its own
    // amplifiers.
    key: 'receivers',
    label: 'Receivers and processors',
    types: [browseType('av_receiver'), browseType('av_processor')],
  },
  { key: 'amplifiers', label: 'Power amps', types: [browseType('power_amp')] },
  { key: 'tvs', label: 'TVs', types: [browseType('tv')] },
  { key: 'projectors', label: 'Projectors', types: [browseType('projector')] },
  { key: 'screens', label: 'Screens', types: [browseType('projector_screen')] },
];

export function categoryByKey(key: CategoryKey): BrowseCategory {
  const found = BROWSE_CATEGORIES.find((c) => c.key === key);
  if (!found) throw new Error(`unknown gear category: ${key}`);
  return found;
}

function typeOf(p: Pick<Product, 'type'>): string {
  return p.type as string;
}

export function productsInCategory(
  products: readonly Product[],
  category: BrowseCategory,
): Product[] {
  const types = new Set(category.types.map((t) => t.type));
  return products.filter((p) => types.has(typeOf(p)));
}

/** `BROWSE_CATEGORIES`, filtered to the ones with at least one product —
 *  order preserved. The home view renders exactly these, in this order. */
export function categoriesWithProducts(products: readonly Product[]): BrowseCategory[] {
  return BROWSE_CATEGORIES.filter((c) => productsInCategory(products, c).length > 0);
}

export function filterByType(products: readonly Product[], type: string | null): Product[] {
  if (!type) return [...products];
  return products.filter((p) => typeOf(p) === type);
}

export function sortByPriceAsc(products: readonly Product[]): Product[] {
  return [...products].sort((a, b) => a.msrpUsd.v - b.msrpUsd.v);
}

/**
 * Four picks spread across price, cheap to premium — brief §5: "sort by
 * price and take the cheapest product of each price quartile". Four or
 * fewer products: every one of them, sorted.
 */
export function quartilePicks(products: readonly Product[]): Product[] {
  const sorted = sortByPriceAsc(products);
  if (sorted.length <= 4) return sorted;
  const picks: Product[] = [];
  for (let i = 0; i < 4; i += 1) {
    picks.push(sorted[Math.floor((i * sorted.length) / 4)]!);
  }
  return picks;
}

export interface PriceBounds {
  min: number;
  max: number;
}

/** Rounded-dollar min/max across `products`, or null for an empty list —
 *  the price-range slider's `min`/`max` (brief §4). */
export function priceBounds(products: readonly Product[]): PriceBounds | null {
  if (products.length === 0) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const p of products) {
    const v = Math.round(p.msrpUsd.v);
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return { min, max };
}

export function filterByPriceRange(
  products: readonly Product[],
  min: number,
  max: number,
): Product[] {
  return products.filter((p) => {
    const v = Math.round(p.msrpUsd.v);
    return v >= min && v <= max;
  });
}
